import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Heart } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function InviteAccept() {
  const { token } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (!user) return; // wait for auth state to resolve before deciding what to show
    if (user === false) return;
    api.get(`/invite/${token}`)
      .then((r) => setInvite(r.data))
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail) || "Invito non trovato"));
  }, [token, user]);

  const accept = async () => {
    setAccepting(true);
    try {
      const { data } = await api.post(`/invite/${token}/accept`);
      toast.success(`Ora collabori su "${data.nome_sposi}"`);
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore nell'accettare l'invito");
    } finally {
      setAccepting(false);
    }
  };

  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse font-wed text-xl" style={{ color: "var(--wed-primary)" }}>Caricamento…</div>
      </div>
    );
  }

  if (user === false) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 grain" data-testid="invite-login-required">
        <div className="w-full max-w-sm text-center bg-white border rounded-3xl p-8" style={{ borderColor: "#e3ddd2" }}>
          <Heart size={32} className="mx-auto mb-4" style={{ color: "var(--wed-primary)" }} />
          <h1 className="font-wed text-2xl mb-3">Sei stato invitato a collaborare</h1>
          <p className="text-sm text-neutral-500 mb-6">Accedi o registrati con l'email a cui è stato inviato questo invito, poi riapri questo link per accettarlo.</p>
          <div className="flex flex-col gap-2">
            <Link to="/login" className="btn-wed w-full" data-testid="invite-login-link">Accedi</Link>
            <Link to="/register" className="btn-outline-wed w-full" data-testid="invite-register-link">Registrati</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6 grain" data-testid="invite-accept-page">
      <div className="w-full max-w-sm text-center bg-white border rounded-3xl p-8" style={{ borderColor: "#e3ddd2" }}>
        <Heart size={32} className="mx-auto mb-4" style={{ color: "var(--wed-primary)" }} />
        {error ? (
          <>
            <h1 className="font-wed text-2xl mb-3">Invito non valido</h1>
            <p className="text-sm text-neutral-500">{error}</p>
          </>
        ) : invite ? (
          <>
            <h1 className="font-wed text-2xl mb-3">Collabora su "{invite.nome_sposi}"</h1>
            <p className="text-sm text-neutral-500 mb-6">
              Sei stato invitato come collaboratore. Accettando, potrai gestire invitati, tavoli, programma e budget di questo matrimonio.
            </p>
            {user.email?.toLowerCase() !== invite.email?.toLowerCase() ? (
              <p className="text-sm text-rose-500" data-testid="invite-email-mismatch">
                Questo invito è per {invite.email}, ma hai effettuato l'accesso come {user.email}. Accedi con l'email corretta per accettarlo.
              </p>
            ) : (
              <button onClick={accept} disabled={accepting} className="btn-wed w-full" data-testid="invite-accept-button">
                {accepting ? "Attendere…" : "Accetta invito"}
              </button>
            )}
          </>
        ) : (
          <p className="text-sm text-neutral-400">Caricamento…</p>
        )}
      </div>
    </div>
  );
}
